// External Packages
import { useEffect, useState } from "react";

// External Components
import { MdOpenInNew, MdRefresh } from "react-icons/md";

// Internal Components
import { requestAdminAPI } from "../../api/posh-api";
import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import LinkPair from "../UI/LinkPair";
import { isoDateToString } from "../../util/format-date";
import { differenceInHours } from "date-fns";
import DropDownBanner from "../UI/DropDownBanner";

export default function VehicleStatus({ vin, trackerId }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  // =================== FETCH SAMSARA INFO ===================
  const fetchReportsHandler = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch-vehicle-reports", {
        vehicles: [{ vin, trackerId }],
      });
      const report = response[0]?.report;

      if (report) setData(report);
      else throw new Error(response[0].error);

      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== USE EFFECT ===================
  useEffect(() => {
    if (vin && trackerId) fetchReportsHandler();
  }, [vin, trackerId]);

  return (
    <div className="grid gap-4 mb-4">
      <Heading
        isBackArrow={false}
        title="Vehicle Status"
        text="Information below is collected from either Linxup or Samsara. 
        To get more information, click external link icon on the right of group heading."
      >
        {trackerId ? (
          <p className="hidden text-sm lg:block text-stone-400">
            Tracker ID: {trackerId}
          </p>
        ) : (
          <p className="hidden warning lg:block">Tracker not installed!</p>
        )}
        <MdRefresh onClick={fetchReportsHandler} className="text-2xl icon" />
        {!loading && trackerId && (
          <a
            href={`https://cloud.samsara.com/o/6005413/devices/${data?.id}/vehicle`}
            target="_blank"
            title="View on Samsara"
          >
            <MdOpenInNew className="icon" />
          </a>
        )}
      </Heading>

      <div className="info">
        <DetailPair
          label="Last Updated"
          classes={
            differenceInHours(new Date(), new Date(data?.time)) > 12
              ? "animate-pulse warning"
              : "text-green-600"
          }
          value={isoDateToString(data?.time, "MM/dd/yyyy hh:mm a")}
          loading={loading}
        />
        <DetailPair
          label="Check Engine"
          classes={
            data?.checkEngine ? "animate-pulse warning" : "text-green-600"
          }
          value={data?.checkEngine ? "On" : "Off"}
          loading={loading}
        />
        <DropDownBanner data={data?.faultCodes}>
          <DetailPair
            label="Fault Codes"
            classes={
              data?.faultCodes?.length > 0
                ? "animate-pulse warning"
                : "text-green-600"
            }
            value={`${data?.faultCodes?.length || 0} Issues`}
            loading={loading}
          />
        </DropDownBanner>
        <DetailPair
          label="Fuel"
          classes={
            parseFloat(data.fuel || 0) < 20 ? "animate-pulse warning" : ""
          }
          value={`${data?.fuel ? data.fuel + "%" : "-"}`}
          loading={loading}
        />
        <DetailPair
          label="Mileage"
          value={`${data?.mileage ? Math.round(data.mileage) + "mi" : "-"}`}
          loading={loading}
        />
        <div className="lg:col-span-2">
          <LinkPair
            label="Location"
            value={data?.address}
            link={
              data?.longitude &&
              data?.latitude &&
              `https://www.google.com/maps/search/?api=1&query=${data?.latitude},${data?.longitude}`
            }
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}
